import React from 'react';
// import { useTranslation } from 'react-i18next';
import { useAuthState } from 'react-firebase-hooks/auth';

import {
  useLogInMutation,
  useLogOutMutation,
  useMeLazyQuery,
} from 'generated/graphql';

import {
  firebaseAppAuth,
  providers,
  useFirebasePersistence,
  firebase,
  FirebaseUserCredential,
} from 'services/firebase';
import 'firebase/compat/auth'; // augmentazione namespace firebase.auth (TS 4.9)
import { firebaseAnalytics } from 'services/firebase/analytics';
import * as Sentry from '@sentry/react';

import { WsConnector } from '_apollo/WsSubscriptionClient';
import { refetchActiveQueries } from '_apollo/refetchActiveQueries';
import {
  apolloClient as client,
  apolloAuthLink as authLink,
} from '_apollo/config';

// ————————————————————————————————————————————————————————————————
// Types
type AuthOpResult = {
  success: boolean;
  requiredEmailVerification?: boolean;
  errors?: any[];
  error?: any;
};

// ————————————————————————————————————————————————————————————————
// Hook
export const useAuthController = () => {
  /** Firebase user + init flag */
  const authTuple =
    ((useAuthState as any)?.(firebaseAppAuth) as any) ||
    ([null, false, undefined] as any);
  const [firebaseUser, initialising] = authTuple as [any, boolean];

  /** Persistenza (local / session) */
  const { persistence } = useFirebasePersistence();

  /** UI flags */
  const [signInProgress, setSignInProgress] = React.useState(false);
  const [signOutProgress, setSignOutProgress] = React.useState(false);
  // Synchronous guard to avoid double signOut when called in parallel
  const signOutLockRef = React.useRef(false);

  /** GraphQL hooks */
  const [logIn] = useLogInMutation({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    refetchQueries: ['Me'],
  });
  const [logOut] = useLogOutMutation({
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    refetchQueries: ['Me'],
  });
  const [
    fetchMe,
    { data: dataMe, loading: fetchMeLoading, refetch: refetchMe },
  ] = useMeLazyQuery({ fetchPolicy: 'no-cache', errorPolicy: 'all' });

  /** Bootstrap: se c’è un firebaseUser, assicurati di fetchare Me */
  React.useEffect(() => {
    if (firebaseUser) fetchMe();
  }, [firebaseUser, fetchMe]);

  /** Listener Firebase → rinfresca Me */
  React.useEffect(() => {
    const unsubAuth = firebaseAppAuth.onAuthStateChanged(() => fetchMe());
    const unsubToken = firebaseAppAuth.onIdTokenChanged(() => fetchMe());
    return () => {
      try {
        typeof unsubAuth === 'function' && unsubAuth();
      } catch {}
      try {
        typeof unsubToken === 'function' && unsubToken();
      } catch {}
    };
  }, [fetchMe]);

  // ————————————————————————————————————————————————————————————————
  // Helpers

  async function safeClearApolloStore(client: any) {
    try {
      // 1) stop polling su tutte le observable queries (se presenti)
      const getOQs = client?.getObservableQueries?.bind(client);
      const entries: Array<[any, any]> = getOQs ? Array.from(getOQs()) : [];
      entries.forEach(([, oq]) => {
        try {
          oq?.stopPolling?.();
        } catch {}
      });

      // 2) Apollo Client >= 3.8 espone client.stop() che cancella le in-flight
      if (typeof client?.stop === 'function') {
        await client.stop();
      }

      // 3) Pulisci senza refetch (clearStore NON re-fetcha e di solito non esplode)
      await client.clearStore();

      // 4) opzionale: GC della cache
      try {
        client?.cache?.gc?.();
      } catch {}
    } catch (e) {
      // Se per qualsiasi motivo arriva comunque l’errore, lo ignoriamo in logout
      // (lo scopo è solo far sparire i dati locali dell’utente)
      // console.warn('safeClearApolloStore error', e);
    }
  }

  // Always LOCAL. "Remember me" (SESSION persistence) was removed on 18/10/2026:
  // Firebase SESSION is per tab, not per browser session, so every link that
  // opens a new tab (verification email, reminders, QR check-in) landed signed
  // out, and it was the precondition of the wake-up auth race (ba6a36bf).
  const setupPersistence = React.useCallback(async () => {
    try {
      await persistence.setLocalPersistence();
    } catch (error) {
      // WebKit can hand back an IndexedDB database whose object stores are
      // missing ("InvalidStateError: Object store cannot be found in the
      // database") - and that database is exactly what Firebase Auth uses
      // for LOCAL persistence. Firebase's own guard only checks that
      // IndexedDB exists, not that it is intact, so the rejection reaches
      // us. Since this runs before every sign-in, an unhandled rejection
      // here aborted login and registration outright on the affected
      // iPhones, with no message on screen. Degrade to in-memory instead:
      // the session is lost on reload, but the user gets in.
      Sentry.captureException(error, {
        tags: { area: 'auth', step: 'setPersistence' },
      });
      try {
        await firebaseAppAuth.setPersistence(
          firebase.auth.Auth.Persistence.NONE,
        );
      } catch {
        // Nothing left to fall back to - carry on with whatever Firebase
        // already has rather than blocking the sign-in.
      }
    }
  }, [persistence]);

  const getAnonymousUserIdToken = React.useCallback(async () => {
    return firebaseUser?.isAnonymous
      ? await firebaseUser.getIdToken()
      : undefined;
  }, [firebaseUser]);

  /** Aggiorna Authorization header + riavvia WS (senza resetStore) */
  const refreshAuthHeaderAndWs = React.useCallback(async () => {
    // Forza token fresco
    const freshToken = await firebaseAppAuth.currentUser
      ?.getIdToken(true)
      .catch(() => undefined);

    // Aggiorna authLink se supporta updateToken
    try {
      if (authLink && typeof authLink.updateToken === 'function') {
        // Se la tua implementazione preferisce un getter asincrono, usa:
        // authLink.updateToken(async () => await firebaseAppAuth.currentUser?.getIdToken());
        authLink.updateToken(freshToken as string | null);
      }
    } catch {}

    // WS reconnect per aggiornare connParams
    try {
      if (WsConnector && typeof WsConnector.emit === 'function') {
        WsConnector.emit('disconnect');
        WsConnector.emit('connect');
      }
    } catch {}
  }, []);

  /** Dopo una login riuscita: aggiorna header, refetcha query attive e Me */
  const postLoginSync = React.useCallback(async () => {
    await refreshAuthHeaderAndWs();

    // Evitiamo resetStore/clearStore (visto il bug con useQuery.complete)
    try {
      await refetchActiveQueries(client);
    } catch {}

    await fetchMe();
  }, [refreshAuthHeaderAndWs, fetchMe]);

  // ————————————————————————————————————————————————————————————————
  // Sign-in con EMAIL LINK

  const _signInWithEmailLink = React.useCallback(
    async (
      email: string,
      emailLink: string,
      invitationId?: string,
    ): Promise<AuthOpResult> => {
      // 0) validazione link
      const isEmailLink = firebaseAppAuth.isSignInWithEmailLink(emailLink);
      if (!isEmailLink)
        return {
          success: false,
          error: { message: 'Invalid SignInWithEmailLink' },
        };

      // 1) Firebase sign-in
      let credential: FirebaseUserCredential;
      try {
        credential = await firebaseAppAuth.signInWithEmailLink(
          email,
          emailLink,
        );
      } catch (error) {
        return { success: false, error };
      }
      if (!credential?.user)
        throw new Error('Invalid EmailLink credential user');
      if (!credential?.additionalUserInfo)
        throw new Error('Invalid EmailLink credential additionalUserInfo');

      // 2) Backend login
      // getIdToken(true) forza un token fresco da Firebase — necessario perché dopo
      // signInWithEmailLink il JWT cached potrebbe avere email_verified: false anche
      // se Firebase ha appena marcato l'email come verificata server-side.
      const tokenID = await credential.user.getIdToken(true);
      const input = {
        idToken: tokenID,
        isNewUser: credential.additionalUserInfo.isNewUser,
        providerId: 'password',
        currentUser: JSON.stringify({
          displayName: credential.user.displayName,
        }),
        redirectUrl: window.location.href,
        invitationId,
      };

      let result: any;
      try {
        result = await logIn({ variables: { input } });
      } catch (err: any) {
        const e = err?.graphQLErrors?.[0] as any;
        if (e?.code === 'email-not-verified') {
          return { success: true, requiredEmailVerification: true, errors: [] };
        }
        return { success: false, error: err, errors: err?.graphQLErrors };
      }

      const gqlErrors = result?.errors;
      if (gqlErrors?.length) {
        const e = gqlErrors[0] as any;
        if (e?.code === 'email-not-verified') {
          return { success: true, requiredEmailVerification: true, errors: [] };
        }
        return { success: false, errors: gqlErrors };
      }

      // 3) Sync client → header/WS/query attive + Me
      await postLoginSync();

      // 4) Analytics
      firebaseAnalytics.logEvent('login', { method: 'email_link' });

      return { success: true, requiredEmailVerification: false, errors: [] };
    },
    [logIn, postLoginSync],
  );

  const signInWithEmailLink = React.useCallback(
    async (email: string, emailLink: string, invitationId?: string) => {
      setSignInProgress(true);
      try {
        return await _signInWithEmailLink(email, emailLink, invitationId);
      } finally {
        setSignInProgress(false);
      }
    },
    [_signInWithEmailLink],
  );

  // ————————————————————————————————————————————————————————————————
  // Sign-out

  const signOut = React.useCallback(async () => {
    // Fast synchronous guard to prevent parallel signOut execution
    if (signOutLockRef.current) return;
    signOutLockRef.current = true;
    if (signOutProgress) return; // legacy state-based guard

    setSignOutProgress(true);
    try {
      await (firebaseAppAuth.signOut && firebaseAppAuth.signOut());
      await (logOut && (logOut as any)());

      // svuota token per le prossime richieste
      try {
        if (authLink && typeof authLink.updateToken === 'function') {
          authLink.updateToken(null);
        }
      } catch {}

      // WS reconnect per “staccare” credenziali
      try {
        if (WsConnector && typeof WsConnector.emit === 'function') {
          WsConnector.emit('disconnect');
          WsConnector.emit('connect');
        }
      } catch {}

      // stop() svuota fetchCancelFns prima di clearStore, così cancelPendingFetches
      // in clearStore è un no-op e non propaga InvariantError(42) agli observer React.
      // resetStore() (precedentemente qui) faceva il contrario: iniettava l'errore
      // nelle sottoscrizioni ancora montate (es. webinar) causando un crash.
      await safeClearApolloStore(client);
      firebaseAnalytics.logEvent('sign_out');
    } finally {
      setSignOutProgress(false);
      signOutLockRef.current = false;
    }
  }, [logOut, signOutProgress]);

  // ————————————————————————————————————————————————————————————————
  // Altri metodi di sign-in

  const signInAnonymously = React.useCallback(async () => {
    await setupPersistence();
    setSignInProgress(true);
    try {
      await firebaseAppAuth.signInAnonymously();
      firebaseAnalytics.logEvent('sign_up', { method: 'signInAnonymously' });
      // Inietta il token fresco nell'authLink e rifà Me DOPO che il token è
      // impostato. Senza questo, i fetchMe scatenati dai listener
      // onAuthStateChanged/onIdTokenChanged partono prima che FirebaseAuthLink
      // abbia settato this.token (getIdToken è async); con il batching di
      // React 18 finiscono nello stesso tick e la query deduplication di
      // Apollo li fonde tutti sulla prima richiesta SENZA token -> Me risponde
      // null -> user resta undefined per sempre. postLoginSync forza il token
      // e poi rifà Me, garantendo che la partecipazione possa essere creata.
      await postLoginSync();
    } finally {
      setSignInProgress(false);
    }
  }, [setupPersistence, postLoginSync]);

  const signInWithGoogle = React.useCallback(async () => {
    // The popup must open within this click. Browsers only allow a popup
    // straight from a user gesture, and the gesture does not survive real
    // async work: awaiting setupPersistence (an IndexedDB write) and the
    // anonymous token before opening it got the popup blocked --
    // auth/popup-blocked, 14 learners in the 30 days to 01/10/2026, Safari
    // first -- and Google-only accounts had no other way in. Firebase itself
    // warms its popup machinery at start-up on Safari and mobile precisely so
    // that only instant awaits stand between the click and window.open.
    //
    // Both are therefore started here and awaited after the popup. They
    // settle while the user picks an account, so the signed-in user is still
    // saved with the persistence chosen here -- including the in-memory
    // fallback setupPersistence applies when WebKit hands back a broken
    // IndexedDB, which is what lets those iPhones log in at all.
    const anonymousUserIdTokenPromise = getAnonymousUserIdToken().catch(
      () => undefined,
    );
    const persistencePromise = setupPersistence();
    const credential = await firebaseAppAuth.signInWithPopup(
      providers['googleProvider'],
    );
    await persistencePromise;
    const anonymousUserIdToken = await anonymousUserIdTokenPromise;

    if (!credential?.user) throw new Error('Invalid google credential user');
    if (!credential?.additionalUserInfo)
      throw new Error('Invalid google credential additionalUserInfo');

    const { isNewUser, providerId } = credential.additionalUserInfo;
    const tokenID = await credential.user.getIdToken();

    await logIn({
      variables: {
        input: {
          anoymousUserIdToken: anonymousUserIdToken,
          idToken: tokenID,
          isNewUser,
          providerId,
          currentUser: JSON.stringify({
            displayName: credential.user.displayName,
          }),
          profile: JSON.stringify(credential.additionalUserInfo.profile),
        },
      },
    }).catch((err: any) => console.error('logIn err', err));

    await postLoginSync();

    if (isNewUser)
      firebaseAnalytics.logEvent('sign_up', { method: 'social', providerId });
    else firebaseAnalytics.logEvent('login', { method: 'social', providerId });
  }, [getAnonymousUserIdToken, setupPersistence, logIn, postLoginSync]);

  const signInWithEmailAndPassword = React.useCallback(
    async (email: string, password: string): Promise<AuthOpResult> => {
      const anonymousUserIdToken = await getAnonymousUserIdToken();
      await setupPersistence();

      let credential: FirebaseUserCredential;
      try {
        credential = await firebaseAppAuth.signInWithEmailAndPassword(
          email,
          password,
        );
      } catch (err: any) {
        const errors: any[] = [];
        if (err?.code === 'auth/user-not-found') {
          errors.push({ name: 'email', code: err.code, message: err.message });
        } else if (err?.code === 'auth/wrong-password') {
          errors.push({
            name: 'password',
            code: err.code,
            message: err.message,
          });
        }
        return { success: false, errors };
      }
      if (!credential?.user)
        throw new Error('Invalid EmailAndPassword credential user');
      if (!credential?.additionalUserInfo)
        throw new Error(
          'Invalid EmailAndPassword credential additionalUserInfo',
        );

      const tokenID = await credential.user.getIdToken();
      const result: any = await logIn({
        variables: {
          input: {
            anoymousUserIdToken: anonymousUserIdToken,
            idToken: tokenID,
            isNewUser: credential.additionalUserInfo.isNewUser,
            providerId: 'password',
            currentUser: JSON.stringify({
              displayName: credential.user.displayName,
            }),
            redirectUrl: window.location.href,
          },
        },
      });

      const loginErrs = result?.errors;
      if (loginErrs?.length) {
        const e = loginErrs[0] as any;
        if (e?.code === 'email-not-verified') {
          return { success: true, requiredEmailVerification: true, errors: [] };
        }
        return { success: false, errors: loginErrs };
      }

      await postLoginSync();
      firebaseAnalytics.logEvent('login', { method: 'email_and_password' });
      return { success: true, requiredEmailVerification: false, errors: [] };
    },
    [getAnonymousUserIdToken, setupPersistence, logIn, postLoginSync],
  );

  const createUserWithEmailAndPassword = React.useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
      codeVerificationMode: boolean = false,
    ): Promise<{
      user?: any;
      success: boolean;
      requiredEmailVerification?: boolean;
      errors?: any[];
    }> => {
      const anonymousUserIdToken = await getAnonymousUserIdToken();
      await setupPersistence();

      let credential: FirebaseUserCredential;
      try {
        credential = await firebaseAppAuth.createUserWithEmailAndPassword(
          email,
          password,
        );
      } catch (error: any) {
        const errors: any[] = [];
        if (error?.code === 'auth/email-already-in-use') {
          errors.push({
            name: 'email',
            code: error.code,
            message: error.message,
          });
        } else if (error?.code === 'auth/wrong-password') {
          errors.push({
            name: 'password',
            code: error.code,
            message: error.message,
          });
        } else {
          errors.push({
            name: 'password',
            code: error?.code,
            message: error?.message,
          });
        }
        return { user: null, errors, success: false };
      }
      if (!credential?.user) throw new Error('Invalid signup credential user');
      if (!credential?.additionalUserInfo)
        throw new Error('Invalid signup credential additionalUserInfo');

      const tokenID = await credential.user.getIdToken();
      const result: any = await logIn({
        variables: {
          input: {
            anoymousUserIdToken: anonymousUserIdToken,
            idToken: tokenID,
            isNewUser: credential.additionalUserInfo.isNewUser,
            providerId: credential.additionalUserInfo.providerId,
            currentUser: JSON.stringify({
              firstName,
              lastName,
              displayName: `${firstName} ${lastName}`,
            }),
            redirectUrl: window.location.href,
            codeVerificationMode,
          },
        },
      });

      const loginErrs2 = result?.errors;
      if (loginErrs2?.length) {
        const e = loginErrs2[0] as any;
        if (e?.code === 'email-not-verified') {
          return { success: true, requiredEmailVerification: true, errors: [] };
        }
        return { success: false, errors: loginErrs2 };
      }

      await postLoginSync();
      firebaseAnalytics.logEvent('sign_up', { method: 'email_and_password' });
      return { success: true, errors: [] };
    },
    [getAnonymousUserIdToken, setupPersistence, logIn, postLoginSync],
  );

  // ————————————————————————————————————————————————————————————————
  // Re-auth

  const reauthenticateWithGoogle = React.useCallback(async () => {
    if (!firebaseUser) throw new Error('current user not found');
    return await firebaseUser.reauthenticateWithPopup(
      providers['googleProvider'],
    );
  }, [firebaseUser]);

  const reauthenticateWithPassword = React.useCallback(
    async (password: string) => {
      if (!firebaseUser) throw new Error('current user not found');
      if (!firebaseUser.email) throw new Error('current user has not email');

      const credentials = firebase.auth.EmailAuthProvider.credential(
        firebaseUser.email,
        password,
      );
      return await firebaseUser.reauthenticateWithCredential(credentials);
    },
    [firebaseUser],
  );

  // ————————————————————————————————————————————————————————————————
  // Me + Analytics

  const user = dataMe?.me;

  React.useEffect(() => {
    if (!user?.id) {
      // Clear Sentry user context on logout / unauthenticated state.
      Sentry.setUser(null);
      return;
    }
    // Identify the user in Sentry so every error includes email + id.
    // Anonymous users have no meaningful email, so we omit it.
    Sentry.setUser(
      user.isAnonymous
        ? { id: user.id }
        : {
            id: user.id,
            email: user.email ?? undefined,
            username:
              `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
              undefined,
          },
    );
    firebaseAnalytics.setUserId(user.id);
    firebaseAnalytics.setUserProperties({
      is_anonymous: user.isAnonymous,
      is_staff: user.isStaff,
      is_superuser: user.isSuperuser,
      email: user.email,
    });
  }, [user]);

  // ————————————————————————————————————————————————————————————————
  // API del hook

  return {
    // stato
    user,
    firebaseUser,
    initialising,
    signInProgress,
    signOutProgress,
    fetchMeLoading,

    // azioni
    signOut,
    signInAnonymously,
    signInWithGoogle,
    signInWithEmailAndPassword,
    signInWithEmailLink,
    createUserWithEmailAndPassword,
    reauthenticateWithGoogle,
    reauthenticateWithPassword,

    // util
    refetchMe,
    firebaseAppAuth,
    providers,
  };
};

// eslint-disable-next-line react-hooks/rules-of-hooks
const useAuthControllerFake = (false as true) && useAuthController();
export type authControllerType = typeof useAuthControllerFake;
export default useAuthController;
